import React, { useEffect, useState } from 'react';
import moment from 'moment';
import CurrencyFormat from 'react-currency-format';
import cn from '@/utils/cn';
import { CloseIcon } from '@/components/Icons';
import Skeleton from '@/components/Skeleton';
import Button from '@/components/Button';
import { batchModal, batchPaymentsPage, scheduledPaymentModal } from '@/utils/translations';
import { DATE_FORMAT_FULL } from '@/utils/dateFormats';
import { buildStatementLine } from '@/utils/buildStatementLine';
import { TPaymentListItem } from '../types/ManagePayments';
import {
  ScheduledBankPaymentGroupStateEnum,
  ScheduledBankPaymentGroupTypeEnum
} from '@/features/payments/types/BatchPayment';
import {
  STATUS_LABEL,
  STATUS_STYLE,
  isActionableBatchSummaryState,
  isBatchAllFailed,
  isBatchIncomplete,
  isScheduledWithoutMultiSign
} from '../data/paymentStatus';
import DetailRow from './DetailRow';
import BatchOverviewModal from './BatchOverviewModal';
import useAuth from '@/hooks/useAuth';
import { useCustomerName } from '../hooks/useCustomerName';
import { useScheduledPaymentDetail } from '../hooks/useScheduledPaymentDetail';
import { useBatchOverviewPayments } from '../hooks/useBatchOverviewPayments';
import { useWalletBalance } from '../hooks/useWalletBalance';
import { useApprovalDisplayState } from '../hooks/useApprovalDisplayState';
import { useScheduledPaymentAction, TPaymentUpdate } from '../hooks/useScheduledPaymentAction';

const TITLE_ID = 'batch-title';

// Compares as integer cents, not raw dollar floats - summing several already-2-decimal amounts
// (see failedAmount below) can drift by fractions of a cent (e.g. 0.10 + 0.20 !== 0.30 in
// floating point), which would otherwise misclassify a balance that exactly covers the failures.
const toCents = (amount: number) => Math.round(amount * 100);

interface BatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: TPaymentListItem | null;
  /** Threaded through to Batch Overview's Reject/Approve - see useScheduledPaymentAction. */
  onPaymentUpdate?: (update: TPaymentUpdate) => void;
  onPaymentDelete?: (id: string) => void;
}

/**
 * A true multi-payee batch needs its own payee-list view behind every action button here
 * (approval/detail flows differ from a single scheduled payment), which is future work - this
 * covers the summary card only. Layout is state-driven: PENDING_APPROVAL shows "Payment details";
 * every other state shows "Batch details". EXPIRED, FAILED, and COMPLETED_WITH_FAILURES each add
 * their own banner (see `banner` below). FAILED and COMPLETED_WITH_FAILURES both get a View
 * payments/Retry payments pair; EXPIRED gets a View payments/Pay now pair too, but Pay now stays
 * disabled - nothing to retry since the batch never went out, and the backend doesn't expose a
 * failure reason to promise a specific fix.
 *
 * Xero batches are the one exception to all of the above: they're managed from Xero, not here,
 * so regardless of state they only ever get a single plain "View payments" - no approve, retry,
 * or any other altering action.
 */
const BatchModal: React.FC<BatchModalProps> = ({
  isOpen,
  onClose,
  payment,
  onPaymentUpdate,
  onPaymentDelete
}) => {
  const createdByName = useCustomerName(payment?.createdByCustomerId);
  const { detail, isLoading } = useScheduledPaymentDetail(payment?.id, isOpen);
  const auth = useAuth();
  // Fetched regardless of decision state so "have I already approved this" and "has anyone at all
  // approved this yet" are both known from the very first render, not just right after clicking
  // Approve inside Batch Overview - see useApprovalDisplayState (shared with ScheduledPaymentModal
  // so the two can't drift out of alignment the way they previously did).
  const {
    hasPersistedApproval,
    hasNoApprovalAuthority,
    isAwaitingAdditionalApproval,
    isCurrentUserApproved,
    isNoApprovalAuthority,
    isApprovalRequiredForViewer,
    isLoadingApprovalDecision
  } = useApprovalDisplayState(payment, isOpen, auth?.customer?.id);
  const { retry, isRetrying, retryError, currentUserName } = useScheduledPaymentAction(payment, {
    onPaymentUpdate,
    onPaymentDelete,
    hasPersistedApproval,
    hasNoApprovalAuthority
  });
  // Fetched for any incomplete (all-failed or partially-failed), not force-completed batch - a
  // batch can run out of money partway through and only its later payments fail, so insufficient
  // funds isn't limited to the all-failed case. The payee list is needed alongside the balance
  // because a partial failure's "amount that actually failed" isn't the batch total - some of it
  // already succeeded and left the wallet.
  const isInsufficientFundsCandidate =
    !!payment && !payment.forceCompleted && isBatchIncomplete(payment.state);
  const { balance: walletBalance, isLoading: isLoadingWalletBalance } = useWalletBalance(
    payment?.walletId,
    isOpen && isInsufficientFundsCandidate
  );
  const { payments: batchPayments, isLoading: isLoadingBatchPayments } = useBatchOverviewPayments(
    payment?.id,
    isOpen && isInsufficientFundsCandidate,
    false,
    true
  );
  const [showOverview, setShowOverview] = useState(false);

  // Reset each time a (possibly different) payment is opened, rather than carrying over "still on
  // the overview screen" from a previous session - except when the payment's own state has
  // nothing actionable on the summary card, in which case it opens straight on the overview
  // instead (see isActionableBatchSummaryState). Only computed at open time, not on every
  // subsequent state change, so a live IN_PROGRESS -> COMPLETED/FAILED transition (see
  // useInProgressGroupPolling) can't yank a user already on the summary card over to the overview
  // mid-session - though in practice IN_PROGRESS never reaches the summary card in the first
  // place, since it's one of the skipped states itself.
  useEffect(() => {
    setShowOverview(
      !!payment && !isActionableBatchSummaryState(payment.state, payment.forceCompleted)
    );
  }, [payment?.id, isOpen]);

  if (!isOpen || !payment) return null;

  if (showOverview) {
    return (
      <BatchOverviewModal
        payment={payment}
        onBack={() => setShowOverview(false)}
        onClose={onClose}
        onPaymentUpdate={onPaymentUpdate}
        onPaymentDelete={onPaymentDelete}
      />
    );
  }

  const isExpired = payment.state === ScheduledBankPaymentGroupStateEnum.EXPIRED;
  // Which of FAILED/COMPLETED_WITH_FAILURES the backend actually returns can't be trusted to
  // mean "all failed" vs "some failed" - see isBatchIncomplete - so that split is computed from
  // the real counts instead.
  const isCompletedWithIssue = isBatchIncomplete(payment.state);
  const isAllFailure = isBatchAllFailed(payment.state, payment.completedPayments);
  const isPartialFailure = isCompletedWithIssue && !isAllFailure;
  const isXero = payment.type === ScheduledBankPaymentGroupTypeEnum.XERO;
  // Only ever true for a row that was previously FAILED/COMPLETED_WITH_FAILURES (Mark as
  // complete only exists for those) - once set, it's terminal, so the failure banner/pill no
  // longer apply.
  const isMarkedComplete = isCompletedWithIssue && !!payment.forceCompleted;
  // The backend has no insufficient-funds state for a batch, so this is inferred: at least one
  // payment failed AND the current wallet balance can't cover the amount that actually failed
  // (not the batch total - a partial failure has already deducted its successful payments from
  // the wallet). Unknown balance/payee list (loading/failed fetch) falls back to the generic
  // all-failed/partial-failure banner.
  const failedAmount = batchPayments
    .filter((row) => row.lastPaymentState === 'failed')
    .reduce((sum, row) => sum + row.amount, 0);
  const isInsufficientFunds =
    isCompletedWithIssue &&
    walletBalance !== null &&
    failedAmount > 0 &&
    toCents(walletBalance) < toCents(failedAmount);

  const banner = isMarkedComplete
    ? null
    : isExpired
      ? { title: batchModal.expiredBannerTitle, body: batchModal.expiredBannerBody }
      : isInsufficientFunds
        ? {
            title: batchModal.partialFailureBannerTitle(
              payment.failedPayments ?? payment.totalPayments ?? 0
            ),
            body: batchModal.insufficientFundsBannerBody
          }
        : isAllFailure
          ? { title: batchModal.allFailureBannerTitle, body: batchModal.allFailureBannerBody }
          : isPartialFailure
            ? {
                title: batchModal.partialFailureBannerTitle(payment.failedPayments ?? 0),
                body: batchModal.partialFailureBannerBody
              }
            : null;

  // An APPROVED batch with no multiSignActionId never actually went through anyone's approval -
  // it was simply scheduled (single-signer rule, or multisign not configured/triggered).
  const isScheduledNotMultiSign = isScheduledWithoutMultiSign(
    payment.state,
    payment.multiSignActionId
  );

  // EXPIRED already reads correctly via the normal state label/colour (both "Expired", red) - only
  // the FAILED/COMPLETED_WITH_FAILURES pill needs its own copy, and even then only when it's a
  // genuine partial failure - an all-failed batch (isAllFailure, same derivation the banner above
  // already uses) shows "Failed" instead of "Processed but incomplete", regardless of whether the
  // backend actually returned FAILED or COMPLETED_WITH_FAILURES for it.
  //
  // isNoApprovalAuthority checked first, kept as defense-in-depth even though
  // useApprovalDisplayState already excludes it from isAwaitingAdditionalApproval at the
  // source (the two are mutually exclusive by construction, not just by this ordering) -
  // see its module comment for why that exclusion lives there rather than here.
  const statusLabel = isMarkedComplete
    ? batchModal.markedAsCompleteLabel
    : isNoApprovalAuthority
      ? batchPaymentsPage.noApprovalAuthorityStatus
      : isAwaitingAdditionalApproval
        ? scheduledPaymentModal.awaitingAdditionalApprovalStatusLabel
        : isAllFailure
          ? batchPaymentsPage.status.failed
          : isPartialFailure
            ? batchModal.processedButIncompleteLabel
            : isScheduledNotMultiSign
              ? batchPaymentsPage.status.scheduled
              : STATUS_LABEL[payment.state];
  const statusStyle = isCompletedWithIssue ? 'text-error-darkred' : STATUS_STYLE[payment.state];
  // Approval is about the specific payment being approved; every other state is about the batch.
  const sectionLabel = isApprovalRequiredForViewer
    ? scheduledPaymentModal.paymentDetails
    : batchModal.batchDetailsLabel;
  // Xero always gets the plain button regardless of state - see the module comment above for why.
  // isApprovalRequiredForViewer already covers "no authority" and "already approved" correctly -
  // see useApprovalDisplayState - so there's nothing further to combine here.
  const singleActionLabel = isXero
    ? batchModal.viewPaymentsButton
    : isApprovalRequiredForViewer
      ? batchModal.viewPaymentsToApproveButton
      : batchModal.viewPaymentsButton;
  // Once force-completed (via Mark as complete in Batch Overview), Retry payments is retired for
  // good - falls back to the single "View payments" button, same as any other no-action state.
  const showRetryPair = isCompletedWithIssue && !isXero && !payment.forceCompleted;
  const showExpiredPair = isExpired && !isXero;
  const handleViewPayments = () => setShowOverview(true);

  const statementValue = isLoading ? (
    <Skeleton
      count={2}
      containerClassName="items-end"
      ariaLabel={scheduledPaymentModal.loadingStatement}
    />
  ) : detail ? (
    <span className="block text-xs">
      {buildStatementLine(detail.payeeParticulars, detail.payeeAnalysis, detail.payeeReference)}
    </span>
  ) : (
    scheduledPaymentModal.emptyValue
  );

  return (
    <div
      className="fixed inset-0 z-99 flex justify-center overflow-y-auto bg-aux-lightgray"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}>
      <button
        type="button"
        onClick={onClose}
        aria-label={scheduledPaymentModal.closeLabel}
        className="absolute right-6 top-6 flex h-[38px] w-[38px] items-center justify-center rounded-full border border-aux-gray bg-white hover:bg-emerge-base-3">
        <CloseIcon onClick={() => {}} size="sm" />
      </button>

      {/* Anchored near the top, matching Pay/Batch Payment/Transfer Money's own modal layout
          (see e.g. src/pages/Home/TransferMoney/TransferMoney.tsx) - not vertically centered. */}
      <div className="w-107.5 max-w-full pt-16">
        <div className="rounded-t-2xl border border-aux-gray bg-emerge-base-3 px-4 py-[18px]">
          <h4 id={TITLE_ID} className="text-center text-2xl font-medium text-emerge-navy">
            {batchModal.title}
          </h4>
        </div>

        <div className="flex flex-col gap-y-4 rounded-b-2xl border-x border-b border-aux-gray bg-white p-6">
          <span className="text-center text-xs font-medium text-emerge-navy">
            {batchModal.totalAmountLabel}
          </span>
          <CurrencyFormat
            value={Number(payment.totalAmount).toFixed(2)}
            displayType="text"
            thousandSeparator
            prefix="$"
            decimalScale={2}
            className="-mt-2 text-center text-5xl font-medium text-emerge-navy"
          />
          <span className={cn('text-center text-[13px] font-medium', statusStyle)}>
            {statusLabel}
          </span>

          {/* Scoped to isCurrentUserApproved (this viewer specifically), not the wider
              isAwaitingAdditionalApproval - currentUserName is always this viewer's own name, so
              crediting it here for someone else's approval would be wrong. */}
          {isCurrentUserApproved && (
            <DetailRow
              label={scheduledPaymentModal.approvedByLabel}
              value={currentUserName}
              filled
            />
          )}

          <span className="text-xs font-medium text-emerge-navy-2">{sectionLabel}</span>
          <div className="flex flex-col gap-y-0.5">
            <DetailRow label={scheduledPaymentModal.createdByLabel} value={createdByName} />
            <DetailRow
              label={scheduledPaymentModal.fromLabel}
              value={payment.payFromAccount?.name ?? scheduledPaymentModal.emptyValue}
              filled
            />
            <DetailRow label={scheduledPaymentModal.statementDetailsLabel} value={statementValue} />
          </div>
          <div className="flex justify-center pt-2">
            <span className="rounded-full bg-emerge-base-3 px-4 py-1 text-2xs font-medium text-emerge-navy">
              {moment(payment.scheduledDate).format(DATE_FORMAT_FULL)}
            </span>
          </div>

          {/* Held until the balance and payee list both resolve, so an incomplete batch never
              shows the generic banner first and then swaps to the insufficient-funds one. */}
          {isCompletedWithIssue &&
          !isMarkedComplete &&
          (isLoadingWalletBalance || isLoadingBatchPayments) ? (
            <div className="rounded-[10px] bg-aux-lightred p-4">
              <Skeleton count={2} className="w-full" ariaLabel={batchModal.loadingBanner} />
            </div>
          ) : (
            banner && (
              <div className="flex flex-col gap-y-1 rounded-[10px] bg-aux-lightred p-4">
                <p className="text-xs font-medium text-error-darkred">{banner.title}</p>
                <p className="whitespace-pre-line text-2xs font-medium text-error-darkred">
                  {banner.body}
                </p>
              </div>
            )
          )}

          {isCurrentUserApproved && (
            <div className="flex flex-col gap-y-1 rounded-[10px] bg-emerge-teal-3 p-4">
              <p className="text-xs font-medium text-emerge-navy">
                {scheduledPaymentModal.approvedBannerTitle}
              </p>
              <p className="text-2xs font-medium text-dark-navy-30">
                {scheduledPaymentModal.approvedBannerBody}
              </p>
            </div>
          )}

          {isLoadingApprovalDecision ? (
            <p role="status" className="mt-6 text-center text-xs font-medium text-aux-gray">
              {scheduledPaymentModal.loadingApprovers}
            </p>
          ) : showRetryPair ? (
            <>
              <div className="mt-6 flex items-center gap-x-4">
                <Button
                  variant="primary_inverted"
                  size="pill"
                  onClick={handleViewPayments}
                  disabled={isRetrying}
                  className="w-auto flex-1">
                  {batchModal.viewPaymentsButton}
                </Button>
                <Button size="pill" onClick={retry} disabled={isRetrying} className="w-auto flex-1">
                  {isRetrying
                    ? scheduledPaymentModal.retryingButton
                    : batchModal.retryPaymentsButton}
                </Button>
              </div>
              {retryError && (
                <p role="alert" className="text-center text-xs font-medium text-error-darkred">
                  {retryError}
                </p>
              )}
            </>
          ) : showExpiredPair ? (
            <div className="mt-6 flex items-center gap-x-4">
              <Button
                variant="primary_inverted"
                size="pill"
                onClick={handleViewPayments}
                className="w-auto flex-1">
                {batchModal.viewPaymentsButton}
              </Button>
              <Button size="pill" disabled className="w-auto flex-1">
                {batchModal.payNowButton}
              </Button>
            </div>
          ) : (
            <Button size="pill" onClick={handleViewPayments} className="mt-6 w-full">
              {singleActionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchModal;
